import { useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import Breadcrumb from "@/components/feature/Breadcrumb";
import NewsCard from "@/components/feature/NewsCard";
import EmptyState from "@/components/feature/EmptyState";
import { news, newsCategories } from "@/mocks/news";
import { useDocumentMeta } from "@/hooks/useDocumentMeta";

const PAGE_SIZE = 6;

export default function News() {
  useDocumentMeta(
    "吴梦梦影视资讯 - 最新动态与评论",
    "吴梦梦影视资讯分类页，汇集影评、行业新闻、活动报道与演员动态，覆盖吴梦梦最新项目进展与作品评论，按发布时间倒序更新。",
    "吴梦梦资讯,影视资讯,影评,行业新闻,演员动态",
  );

  const [searchParams, setSearchParams] = useSearchParams();
  const categoryParam = searchParams.get("category") ?? "全部";
  const category = newsCategories.includes(categoryParam) ? categoryParam : "全部";

  const [keyword, setKeyword] = useState("");
  const [visible, setVisible] = useState(PAGE_SIZE);

  useEffect(() => {
    setVisible(PAGE_SIZE);
  }, [category, keyword]);

  const changeCategory = (next: string) => {
    if (next === "全部") {
      setSearchParams({});
    } else {
      setSearchParams({ category: next });
    }
  };

  const filtered = useMemo(() => {
    const term = keyword.trim().toLowerCase();
    return [...news]
      .filter((item) => {
        if (category !== "全部" && item.category !== category) return false;
        if (!term) return true;
        return (
          item.title.toLowerCase().includes(term) ||
          item.excerpt.toLowerCase().includes(term) ||
          item.author.toLowerCase().includes(term) ||
          item.category.toLowerCase().includes(term)
        );
      })
      .sort((a, b) => b.date.localeCompare(a.date));
  }, [category, keyword]);

  const displayed = filtered.slice(0, visible);

  const resetAll = () => {
    setKeyword("");
    setSearchParams({});
  };

  return (
    <div>
      <section className="border-b border-background-200 bg-background-100/60">
        <div className="container-content py-7 md:py-11">
          <Breadcrumb items={[{ label: "首页", to: "/" }, { label: "资讯" }]} />
          <div className="mt-4 md:mt-5 flex flex-col lg:flex-row lg:items-end justify-between gap-5">
            <div className="min-w-0">
              <span className="text-[10px] md:text-[11px] font-label tracking-[0.22em] text-accent-600 uppercase">
                News & Review
              </span>
              <h1 className="mt-2 text-2xl md:text-4xl font-bold leading-tight">影视资讯</h1>
              <p className="mt-3 max-w-2xl text-sm text-foreground-600 leading-relaxed">
                汇集影评、行业新闻、活动报道与演员动态四类内容，记录每一部作品的台前幕后，也记录一个演员在片场之外的日常。
              </p>
            </div>
            <div className="shrink-0 grid grid-cols-2 sm:grid-cols-4 gap-3 lg:gap-5">
              {newsCategories.slice(1).map((item) => (
                <button
                  key={item}
                  type="button"
                  onClick={() => changeCategory(item)}
                  className="text-left cursor-pointer group"
                >
                  <div className="font-label text-xl font-extrabold text-primary-600 group-hover:text-accent-600 transition-colors">
                    {news.filter((entry) => entry.category === item).length}
                  </div>
                  <div className="mt-1 text-[11px] text-foreground-500 whitespace-nowrap">{item}</div>
                </button>
              ))}
            </div>
          </div>
        </div>
      </section>

      <section className="container-content py-7 md:py-10">
        <div className="rounded-lg border border-background-200 bg-background-50 p-4 md:p-5">
          <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-4">
            <div className="flex flex-wrap items-center gap-1.5">
              {newsCategories.map((item) => (
                <button
                  key={item}
                  type="button"
                  onClick={() => changeCategory(item)}
                  className={`px-3.5 h-8 rounded-full text-xs transition-colors cursor-pointer whitespace-nowrap ${
                    category === item
                      ? "bg-primary-500 text-background-50 font-medium"
                      : "bg-background-100 text-foreground-600 hover:text-foreground-950"
                  }`}
                >
                  {item}
                </button>
              ))}
            </div>

            <form
              onSubmit={(event) => event.preventDefault()}
              className="relative w-full lg:w-72 shrink-0"
            >
              <i className="ri-search-line absolute left-3 top-1/2 -translate-y-1/2 text-foreground-400 text-sm leading-none" />
              <input
                type="search"
                value={keyword}
                onChange={(event) => setKeyword(event.target.value)}
                placeholder="在当前资讯中搜索"
                aria-label="搜索资讯"
                className="w-full h-10 pl-9 pr-3 rounded-md bg-background-100 border border-background-200 text-sm text-foreground-900 placeholder:text-foreground-400 focus:outline-none focus:border-primary-300 focus:bg-background-50"
              />
            </form>
          </div>

          {(keyword || category !== "全部") && (
            <div className="mt-3.5 pt-3.5 border-t border-background-200 flex flex-wrap items-center gap-2">
              <span className="text-[11px] text-foreground-500">当前筛选：</span>
              {category !== "全部" && (
                <span className="inline-flex items-center gap-1 h-6 px-2 rounded-full bg-secondary-100 text-secondary-900 text-[11px]">
                  分类 · {category}
                </span>
              )}
              {keyword.trim() && (
                <span className="inline-flex items-center gap-1 h-6 pl-2 pr-1 rounded-full bg-accent-100 text-accent-900 text-[11px]">
                  关键词 · {keyword.trim()}
                  <button
                    type="button"
                    onClick={() => setKeyword("")}
                    aria-label="清除关键词"
                    className="w-4 h-4 flex items-center justify-center rounded-full hover:bg-accent-200 cursor-pointer"
                  >
                    <i className="ri-close-line text-[11px] leading-none" />
                  </button>
                </span>
              )}
              <button
                type="button"
                onClick={resetAll}
                className="text-[11px] text-foreground-500 hover:text-accent-600 cursor-pointer underline underline-offset-2"
              >
                清除全部
              </button>
              <span className="ml-auto text-[11px] text-foreground-500 font-label">
                {filtered.length} 条结果
              </span>
            </div>
          )}
        </div>

        <div className="mt-6 md:mt-8">
          {displayed.length === 0 ? (
            <EmptyState
              icon="ri-newspaper-line"
              title="没有匹配的资讯"
              description="当前分类与关键词组合下暂时没有内容。可以清除搜索关键词，或切换到其他资讯分类看看。"
              actionLabel="清除筛选条件"
              onAction={resetAll}
            />
          ) : (
            <>
              <div className="flex flex-col divide-y divide-background-200">
                {displayed.map((item) => (
                  <div key={item.id} className="py-5 first:pt-0">
                    <NewsCard news={item} />
                  </div>
                ))}
              </div>

              <div className="mt-9 flex flex-col items-center gap-3">
                <p className="text-xs text-foreground-500 font-label">
                  已显示 {displayed.length} / {filtered.length} 条资讯
                </p>
                {visible < filtered.length ? (
                  <button
                    type="button"
                    onClick={() => setVisible((count) => count + 3)}
                    className="inline-flex items-center gap-2 h-11 px-7 rounded-md border border-background-300 bg-background-50 text-sm font-medium text-foreground-800 hover:border-primary-400 hover:text-primary-600 transition-colors cursor-pointer whitespace-nowrap"
                  >
                    加载更多资讯
                    <i className="ri-arrow-down-line text-base leading-none" />
                  </button>
                ) : (
                  <span className="inline-flex items-center gap-1.5 text-xs text-foreground-400">
                    <i className="ri-check-line text-sm leading-none" />
                    已经到底了
                  </span>
                )}
              </div>
            </>
          )}
        </div>
      </section>
    </div>
  );
}